import { Alert, Button, Divider, Stack, TextField } from "@mui/material";
import { useIsModificationReasonSatisfied } from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { getCaseQueryKey } from "../api/useCase";
import { useUpdateCase } from "../api/useUpdateCase";
import { useUpdatePatient } from "../api/useUpdatePatient";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { getHomeHealthCaseMobileDetailPath } from "../paths";
import { type Case } from "../types";
import { MobileCreateCaseStep3, type Step3Data } from "./MobileCreateCaseStep3";

interface EditCaseFormData extends Step3Data {
  patientName: string;
  address: string;
}

const buttonBaseSx = {
  flex: 1,
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

function haveSameSpecialties(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

interface Props {
  case: Case;
}

export function MobileEditCaseForm({ case: caseItem }: Props) {
  const history = useHistory();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();

  const caseId = caseItem.id;
  const patientId = caseItem.attributes.patient.id;

  const [initialFormData] = useState<EditCaseFormData>(() => ({
    patientName: caseItem.attributes.patient.attributes.externalPatientIdentifier,
    address: caseItem.attributes.patient.attributes.formattedAddress,
    specialties: caseItem.attributes.specialties,
    description: caseItem.attributes.description,
    modificationReason: "",
  }));
  const [formData, setFormData] = useState<EditCaseFormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Local flag rather than deriving from the two mutation `isLoading` states:
  // those both read false in the gap between the sequential patient and case
  // mutations, which would briefly re-enable Save and allow a duplicate submit.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: updatePatient } = useUpdatePatient(patientId);
  const { mutateAsync: updateCase } = useUpdateCase(caseId);

  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(
    formData.modificationReason
  );

  function handleChange(field: keyof EditCaseFormData, value: string | string[]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  const isPatientNameChanged = formData.patientName !== initialFormData.patientName;
  const isAddressChanged = formData.address !== initialFormData.address;
  const isDescriptionChanged = formData.description !== initialFormData.description;
  const areSpecialtiesChanged = !haveSameSpecialties(
    formData.specialties,
    initialFormData.specialties
  );
  const isPatientChanged = isPatientNameChanged || isAddressChanged;
  const isCaseChanged = isDescriptionChanged || areSpecialtiesChanged;
  const hasChanges = isPatientChanged || isCaseChanged;

  async function handleSubmit() {
    setSubmitError(null);
    setIsSubmitting(true);
    const { modificationReason } = formData;
    try {
      if (isPatientChanged) {
        await updatePatient({
          data: {
            attributes: {
              ...(isPatientNameChanged ? { externalPatientIdentifier: formData.patientName } : {}),
              ...(isAddressChanged ? { formattedAddress: formData.address } : {}),
              modificationReason,
            },
            type: "patient",
          },
        });
        logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_PATIENT_UPDATED, {
          workplaceId,
          patientId,
          updatedFields: [
            ...(isPatientNameChanged ? ["externalPatientIdentifier"] : []),
            ...(isAddressChanged ? ["formattedAddress"] : []),
          ],
          performedByAdmin: isAdmin,
        });
      }

      if (isCaseChanged) {
        await updateCase({
          data: {
            attributes: {
              ...(areSpecialtiesChanged ? { specialties: formData.specialties } : {}),
              ...(isDescriptionChanged ? { description: formData.description } : {}),
              modificationReason,
            },
            type: "case",
          },
        });
        logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_CASE_UPDATED, {
          workplaceId,
          caseId,
          patientId,
          updatedFields: [
            ...(areSpecialtiesChanged ? ["specialties"] : []),
            ...(isDescriptionChanged ? ["description"] : []),
          ],
          performedByAdmin: isAdmin,
        });
      }

      // A patient-only edit doesn't invalidate the case query (which embeds the
      // patient and is what the case detail page reads from), so refresh it here.
      await queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId }));
      history.replace(getHomeHealthCaseMobileDetailPath(caseId));
    } catch {
      setSubmitError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  }

  const paddingX = 3;

  return (
    <Stack
      sx={{
        // Fill (not exceed) the fixed-height, overflow-hidden page wrapper so the
        // header and footer stay pinned and only the middle section scrolls.
        // minHeight:"100vh" let tall content grow the column past the clipped
        // parent, pushing the footer (Save) out of view on small screens.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.baseDark",
        paddingBottom: "4rem",
      }}
    >
      <Stack sx={{ px: paddingX, pt: 4, gap: 3 }}>
        <Title component="h5">Edit case</Title>
        <Divider />
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: paddingX, py: 3 }} spacing={3}>
        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Stack spacing={1}>
          <Text semibold variant="body1">
            Patient name
          </Text>
          <TextField
            fullWidth
            value={formData.patientName}
            onChange={(event) => {
              handleChange("patientName", event.target.value);
            }}
            sx={inputSx}
            inputProps={{ "data-dd-privacy": "mask" }}
          />
        </Stack>

        <Stack spacing={1}>
          <Text semibold variant="body1">
            Address
          </Text>
          <TextField
            fullWidth
            value={formData.address}
            onChange={(event) => {
              handleChange("address", event.target.value);
            }}
            sx={inputSx}
            inputProps={{ "data-dd-privacy": "mask" }}
          />
        </Stack>

        <MobileCreateCaseStep3 data={formData} onChange={handleChange} />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ px: paddingX, pb: 4, pt: 2 }}>
        <Button
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace(getHomeHealthCaseMobileDetailPath(caseId));
            }
          }}
          sx={{ ...buttonBaseSx }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isSubmitting || !hasChanges || !isModificationReasonSatisfied}
          onClick={() => {
            void handleSubmit();
          }}
          sx={{ ...buttonBaseSx }}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}
